import { transformAmount as transformPaywallAmount } from "@circle-react/helpers/paywallCustomizationAmountHelpers";
import {
  DETAILED_BACKGROUND_COLOR,
  PRICE_CALLOUT_DEFAULT_COLOR,
  isRedirectToType,
  isRemainingTimeType,
} from "@circle-react/helpers/paywallCustomizationHelpers";
import type {
  DetailedProperties,
  PaywallCustomization,
} from "@circle-react/types/PaywallCustomization";
import {
  ACTION_TYPE,
  COUNTDOWN_TYPE,
  type CountdownProperties,
  type PriceCalloutProperties,
  type ReducedPrice,
  type ReducedPriceProperties,
} from "@circle-react/types/PaywallCustomization";
import { compact } from "lodash";

import type { TestimonialProperty } from "@/react/types/PaywallCustomization";

const getValidTestimonials = (
  testimonial: TestimonialProperty,
): TestimonialProperty | null => {
  const { name, headline, quote } = testimonial;

  const isBlank = (val: unknown) =>
    val === undefined || val === null || val === "";

  if (isBlank(name) && isBlank(headline) && isBlank(quote)) {
    return null;
  }

  return testimonial;
};

const serializeCountdownProperties = (
  countdownProperties: Partial<CountdownProperties>,
): CountdownProperties => ({
  enabled: countdownProperties?.enabled ?? false,
  displayed_text: countdownProperties?.displayed_text || "",
  action: isRedirectToType(countdownProperties?.action?.action_type)
    ? {
        action_type: ACTION_TYPE.REDIRECT_TO,
        redirect_to: countdownProperties?.action?.redirect_to,
      }
    : {
        action_type: ACTION_TYPE.DO_NOTHING,
      },
  ...(isRemainingTimeType(countdownProperties?.countdown_type)
    ? {
        countdown_type: COUNTDOWN_TYPE.REMAINING_TIME,
        remaining_time: Number(countdownProperties?.remaining_time),
        remaining_time_interval: "minutes",
      }
    : {
        countdown_type: COUNTDOWN_TYPE.FINISH_AT,
        finish_at: countdownProperties?.finish_at
          ? new Date(countdownProperties.finish_at).toISOString()
          : new Date().toISOString(),
      }),
});

const serializePriceCalloutProperties = (
  priceCalloutProperties?: Partial<PriceCalloutProperties>,
): PriceCalloutProperties => ({
  callout_color:
    priceCalloutProperties?.callout_color ?? PRICE_CALLOUT_DEFAULT_COLOR,
  prices: priceCalloutProperties?.prices ?? [],
});

export const transformAmount = (input: string | number | undefined | null) =>
  transformPaywallAmount(input);

const serializeReducedPriceProperties = (
  reducedPriceProperties: Partial<ReducedPriceProperties>,
): ReducedPriceProperties => ({
  prices: (reducedPriceProperties?.prices || []).map(
    (reducedPrice: ReducedPrice) => ({
      price_id: reducedPrice.price_id,
      amount: transformAmount(reducedPrice.amount),
      upfront_amount: transformAmount(reducedPrice.upfront_amount),
      enabled: reducedPrice.enabled,
    }),
  ),
});

const serializeDetailedProperties = (
  detailedProperties?: Partial<DetailedProperties>,
): DetailedProperties => ({
  title: detailedProperties?.title || "",
  description: detailedProperties?.description || "",
  background_color:
    detailedProperties?.background_color || DETAILED_BACKGROUND_COLOR,
  cover: detailedProperties?.cover,
  cover_video_properties: detailedProperties?.cover?.content_type?.match(
    /video\//,
  )
    ? {
        ...(detailedProperties?.cover_video_properties || {}),
      }
    : undefined,
});

const serializePaywallCustomization = (
  paywallCustomization: PaywallCustomization,
) => ({
  ...paywallCustomization,
  detailed_properties:
    paywallCustomizationSerializer.serializeDetailedProperties(
      paywallCustomization.detailed_properties,
    ),
  reduced_price_properties:
    paywallCustomizationSerializer.serializeReducedPriceProperties(
      paywallCustomization.reduced_price_properties,
    ),
  price_callout_properties:
    paywallCustomizationSerializer.serializePriceCalloutProperties(
      paywallCustomization.price_callout_properties,
    ),
  countdown_properties:
    paywallCustomizationSerializer.serializeCountdownProperties(
      paywallCustomization.countdown_properties,
    ),
  testimonial_properties: {
    enabled: paywallCustomization?.testimonial_properties?.enabled || false,
    testimonials: compact(
      (paywallCustomization?.testimonial_properties?.testimonials || []).map(
        getValidTestimonials,
      ),
    ),
  },
});

export const paywallCustomizationSerializer = {
  serialize: serializePaywallCustomization,
  serializeDetailedProperties,
  serializeCountdownProperties,
  serializePriceCalloutProperties,
  serializeReducedPriceProperties,
};
